import { useCallback, useMemo } from "react";
import { convertStringToDate } from "./utils";
import {
  DateFieldSingleChange,
  DateFieldSingleProps,
} from "../DateFieldSingle";
import { DateTime, WeekdayNumbers } from "luxon";

export type DateFieldSingleConversion = {
  /**
   * The current value of the date field.
   */
  value?: DateTime | null;
  /**
   * The default (uncontrolled) value of the date field.
   */
  defaultValue?: DateTime | null;
  /**
   * The minimum date of the date field.
   */
  minDate?: DateTime | null;
  /**
   * The maximum date of the date field.
   */
  maxDate?: DateTime | null;
  /**
   * The unavailable dates of the date field.
   */
  unavailable: {
    /**
     * The unavailable dates of the date field.
     */
    dates?: DateTime[];
    /**
     * The unavailable days of the week of the date field.
     */
    daysOfWeek?: WeekdayNumbers[];
  };
  /**
   * The callback for when the date changes.
   */
  onChange: (
    change: Omit<DateFieldSingleChange, "date"> & { date: DateTime | null },
  ) => void;
};

/**
 * Converts the props of the DateFieldSingle component to Luxon DateTime objects for easier internal use.
 * @param props - The props of the DateFieldSingle component using ISO 8601 strings.
 * @returns The converted props using Luxon DateTime objects.
 */
export function useDateFieldSingleConversion({
  value,
  defaultValue,
  minDate,
  maxDate,
  unavailable,
  onChange,
}: DateFieldSingleProps): DateFieldSingleConversion {
  const normalizedValue = useMemo(() => convertStringToDate(value), [value]);
  const normalizedDefaultValue = useMemo(
    () => convertStringToDate(defaultValue),
    [defaultValue],
  );
  const normalizedMinDate = useMemo(
    () => convertStringToDate(minDate),
    [minDate],
  );
  const normalizedMaxDate = useMemo(
    () => convertStringToDate(maxDate),
    [maxDate],
  );
  const normalizedUnavailableDates = useMemo(
    () =>
      unavailable?.dates
        ?.map((d) => convertStringToDate(d))
        .filter((d) => d !== null && d !== undefined),
    [unavailable?.dates],
  );

  const handleChange = useCallback(
    (
      change: Omit<DateFieldSingleChange, "date"> & { date: DateTime | null },
    ) => {
      onChange?.({
        ...change,
        date: change.date?.toISODate() ?? null,
      });
    },
    [onChange],
  );

  return {
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    minDate: normalizedMinDate,
    maxDate: normalizedMaxDate,
    unavailable: {
      dates: normalizedUnavailableDates,
      daysOfWeek: unavailable?.daysOfWeek,
    },
    onChange: handleChange,
  };
}
